import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { TimeOut } from '~/services/apis/timeout'
import { storeToRefs, type StoreGeneric } from 'pinia'
import StringMethods from '~/utils/character'
import dayjs from 'dayjs'

/**
 * Parses a URL query string into a key-value object.
 *
 * @param {string} queryString - The URL query string to parse (with or without leading '?').
 * @returns {{ [id: string]: string }} An object representing the parsed query parameters.
 *
 * @example
 * const result = queryStringToObject('?name=John&age=30');
 * // result = { name: 'John', age: '30' }
 */
export function queryStringToObject(queryString: string) {
	// Remove the leading '?' if it exists
	if (queryString.startsWith('?')) {
		queryString = queryString.substring(1)
	}

	// Split the query string into key-value pairs
	const pairs = queryString.split('&')
	const result: { [id: string]: string } = {}

	// Iterate over the pairs and split them into key and value
	pairs.forEach(pair => {
		const [key, value] = pair.split('=')
		result[key as string] = decodeURIComponent(value || '')
	})

	return result
}

/**
 * Creates a new object by omitting the specified keys from the original object.
 *
 * @template T - The expected return type of the filtered object.
 * @param {{ [id: string]:unknown }} obj - The original object to filter.
 * @param {string[]} keysToExclude - An array of string keys to remove from the object.
 * @returns {T} A new object without the excluded keys.
 *
 * @example
 * const user = { id: 1, name: 'John', password: '123' };
 * const safeUser = filterObject(user, ['password']);
 * // safeUser = { id: 1, name: 'John' }
 */
export function filterObject<T>(obj: Record<string, unknown>, keysToExclude: string[]) {
	return Object.keys(obj)
		.filter(key => !keysToExclude.includes(key)) // Filter out the keys to exclude
		.reduce((result: Record<string, unknown>, key) => {
			result[key] = obj[key] // Construct the result object
			return result
		}, {}) as T
}

/**
 * Calculates a person's age based on their date of birth.
 * Returns the age in years, months, or days depending on the difference.
 *
 * @param {string} dateOfBirth - The date of birth as a string (parsable by Date).
 * @returns {string} The calculated age formatted as a string (e.g., '5 years', '2 months', '10 days').
 *
 * @example
 * const age = calculateAge('1990-01-01');
 * // age = '36 years' (assuming current year is 2026)
 */
export function calculateAge(dateOfBirth: string) {
	const now = new Date()
	const birthDate = new Date(dateOfBirth)

	let yearsDiff = now.getFullYear() - birthDate.getFullYear()
	let monthsDiff = now.getMonth() - birthDate.getMonth()
	let daysDiff = now.getDate() - birthDate.getDate()

	// Adjust the year difference if the birth date hasn't occurred yet this year
	if (monthsDiff < 0 || (monthsDiff === 0 && daysDiff < 0)) {
		yearsDiff--
		monthsDiff += 12
	}

	// Adjust the month difference if necessary
	if (daysDiff < 0) {
		monthsDiff--
		daysDiff += new Date(now.getFullYear(), now.getMonth(), 0).getDate() // Get the number of days in the previous month
	}

	if (yearsDiff > 0) {
		return `${yearsDiff} ${yearsDiff === 1 ? 'year' : 'years'}`
	} else if (monthsDiff > 0) {
		return `${monthsDiff} ${monthsDiff === 1 ? 'month' : 'months'}`
	} else if (daysDiff > 0) {
		return `${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`
	} else {
		return '0 days'
	}
}

/**
 * Compares two objects and returns a new object containing only the keys where the values differ.
 *
 * @template T - The expected return type of the resulting difference object.
 * @param {RecordSchema} obj1 - The first object to compare.
 * @param {RecordSchema} obj2 - The second object to compare against.
 * @returns {T} An object containing the differences.
 *
 * @example
 * const obj1 = { a: 1, b: 2, c: 3 };
 * const obj2 = { a: 1, b: 5, c: 3 };
 * const diff = objectDiff(obj1, obj2);
 * // diff = { b: 2 }
 */
export function objectDiff<T = Record<string, unknown>>(obj1: Record<string, unknown>, obj2: Record<string, unknown>) {
	const result: Record<string, unknown> = {}

	Object.keys(obj1).forEach(key => {
		if (Object.prototype.hasOwnProperty.call(obj2, key)) {
			if (obj1[key] && obj2[key]) {
				if (obj1[key] !== obj2[key]) {
					result[key] = obj1[key]
				}
			}
		}
	})

	return result as T
}
/**
 * Encrypts data by converting it to a Base64 encoded string.
 * Objects are stringified before being encoded.
 *
 * @param {unknown} arg - The data to encrypt.
 * @returns {string} The Base64 encoded string representing the data.
 *
 * @example
 * const encrypted = encryptData('secret');
 * // encrypted = 'c2VjcmV0'
 */
export const encryptData = (arg: unknown) => {
	const parameter = typeof arg === 'string' ? arg : JSON.stringify(arg) || ''
	return btoa(parameter)
}

/**
 * Wraps an asynchronous or synchronous function execution, optionally managing a loading state.
 * Returns the result, or an object containing the error if an exception occurs.
 *
 * @template T - The expected return type.
 * @param {() => T | Promise<T>} fn - The function to execute.
 * @param {Ref<boolean> | boolean} [loading] - Optional Vue Ref or boolean to track loading state.
 * @param {number} [delay=0] - Optional delay in milliseconds before executing the function.
 * @returns {Promise<T>} A promise that resolves to the function's result or an error object.
 *
 * @example
 * const result = await resolve(() => fetch('/api/data'), isLoading);
 * // result = { data: ... } or { error: ..., data: null }
 */
export const resolve = async <T>(fn: () => T | Promise<T>, loading?: Ref<boolean> | boolean, delay = 0): Promise<T> => {
	let result: T
	const setLoading = (arg: boolean) => {
		if (typeof loading === 'boolean') {
			loading = arg
		} else if (loading?.value !== undefined) {
			if (typeof loading.value === 'boolean') {
				loading.value = arg
			}
		}
	}
	try {
		setLoading(true)
		await TimeOut(delay)
		result = await fn()
	} catch (error) {
		console.error('Something Went Wrong ====>', error)
		result = { error, data: null } as T
	} finally {
		setLoading(false)
	}
	return result
}
/**
 * Executes a function and catches any errors, logging them to the console.
 *
 * @template T - The expected return type.
 * @param {(...args:unknown[]) => T} fn - The function to execute.
 * @returns {T | undefined} The result of the function, or undefined if an error occurred.
 *
 * @example
 * const result = catchError(() => JSON.parse('invalid'));
 * // result = undefined (and logs the error)
 */
export const catchError = <T>(fn: (...args: unknown[]) => T) => {
	try {
		return fn()
	} catch (error) {
		console.error(error)
	}
}
/**
 * Checks if a given string is a valid JSON.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} True if the string is valid JSON, false otherwise.
 *
 * @example
 * const isValid = isValidJSON('{"key": "value"}');
 * // isValid = true
 */
export const isValidJSON = (str: string) => {
	try {
		JSON.parse(str)
		return true
	} catch (e) {
		return false
	}
}
/**
 * Checks if a string is a valid Base64 encoded string.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} True if the string can be successfully decoded using atob, false otherwise.
 *
 * @example
 * const isEncryptedString = isEncrypted('c2VjcmV0');
 * // isEncryptedString = true
 */
export const isEncrypted = (str: string): boolean => {
	try {
		atob(str)
		return true
	} catch (e) {
		return false
	}
}
/**
 * Asynchronously encrypts data (string or object) into a Base64 string.
 *
 * @param {object | string} arg - The data to encrypt.
 * @returns {Promise<string>} A promise that resolves to the Base64 encoded string.
 *
 * @example
 * const encoded = await encrypt({ id: 1 });
 * // encoded = 'eyJpZCI6MX0='
 */
export const encrypt = (arg: object | string) => {
	let parameter = arg as string
	return resolve(() => {
		if (typeof arg === 'object') {
			parameter = JSON.stringify(arg)
		}
		return btoa(parameter)
	})
}
/**
 * Decrypts a Base64 encoded string or parses a JSON string back to its original type.
 * If it's neither, returns the original string.
 *
 * @template T - The expected return type.
 * @param {string} arg - The string to decrypt or parse.
 * @returns {T} The decrypted or parsed data.
 *
 * @example
 * const data = decryptString<{ id: number }>('eyJpZCI6MX0=');
 * // data = { id: 1 }
 */
export const decryptString = <T>(arg: string) => {
	if (isEncrypted(arg)) {
		const value = atob(arg)
		if (isValidJSON(value)) {
			return JSON.parse(value) as T
		}
		return value as unknown as T
	} else if (isValidJSON(arg)) {
		return JSON.parse(arg) as T
	}
	return arg as unknown as T
}
/**
 * Retrieves a value from local storage and optionally decrypts/parses it.
 * Only works in a browser environment.
 *
 * @template T - The expected return type.
 * @param {string} key - The key of the local storage item.
 * @param {boolean} [decrypt] - Whether to decrypt/parse the retrieved value.
 * @returns {T | null} The retrieved value, or null if not found or not in a browser.
 *
 * @example
 * const user = getLocalStorage('user', true);
 */
export const getLocalStorage = <T>(key: string, decrypt?: boolean) => {
	if (import.meta.client && typeof window !== 'undefined') {
		const value = localStorage.getItem(key)
		if (decrypt && value) {
			return decryptString<T>(value)
		}
		return value as unknown as T
	}
	return null
}
/**
 * Saves a value to local storage, optionally encrypting it first.
 * Only works in a browser environment.
 *
 * @param {object} opt - Options for setting the storage item.
 * @param {string} opt.key - The key for the storage item.
 * @param {string | object} opt.value - The value to store.
 * @param {boolean} [opt.encrypt] - Whether to encrypt the value before storing.
 * @returns {Promise<void>}
 *
 * @example
 * await setStorageItem({ key: 'user', value: { id: 1 }, encrypt: true });
 */
export const setStorageItem = async (opt: { key: string; value: string | object; encrypt?: boolean }) => {
	if (import.meta.client && typeof window !== 'undefined') {
		let value = opt.value as string
		if (opt.encrypt) {
			value = await encrypt(opt.value)
		}
		localStorage.setItem(opt.key, value)
	}
}
/**
 * Removes an item from local storage.
 * Only works in a browser environment.
 *
 * @param {string} key - The key of the storage item to remove.
 *
 * @example
 * removeStorageItem('user');
 */
export const removeStorageItem = (key: string) => {
	if (import.meta.client && typeof window !== 'undefined') {
		localStorage.removeItem(key)
	}
}
/**
 * Extracts the first query parameter value from a given URL string.
 *
 * @param {string} arg - The URL string.
 * @returns {string} The value of the first query parameter, or an empty string if none.
 *
 * @example
 * const val = extractQueryValue('https://example.com?id=123');
 * // val = '123'
 */
export const extractQueryValue = (arg: string) => {
	if (arg) {
		const url = new URL(String(arg))
		return url.search?.split('=')[1]
	}
	return ''
}
/**
 * Checks if all elements in the first array are present in the second array.
 *
 * @param {string[]} arg - The array of items to check for.
 * @param {string[]} compare - The array to check against.
 * @returns {boolean} True if all items in `arg` are in `compare`, false otherwise.
 *
 * @example
 * const isMatch = isArrayMatch(['a', 'b'], ['a', 'b', 'c']);
 * // isMatch = true
 */
export const isArrayMatch = (arg: string[], compare: string[]) => {
	return arg.every(item => compare.includes(item))
}
/**
 * Checks if a value exists more than once in an array of objects
 * @param list - Array of objects to check
 * @param keyPath - Dot-notation path to the property (e.g., 'employee.value')
 * @param value - Value to check for duplicates
 * @returns boolean indicating if the value appears more than once
 *
 * @example
 * const items = [{ id: 1 }, { id: 2 }, { id: 1 }];
 * const hasDup = isArrayDuplicated(items, 'id', 1);
 * // hasDup = true
 */
export const isArrayDuplicated = <T = Record<string, unknown>>(list: T[], keyPath: string, value: unknown): boolean => {
	if (!list?.length || !keyPath) return false

	const getNestedValue = (obj: unknown, path: string): unknown => {
		return path.split('.').reduce((o: unknown, p) => {
			if (o && typeof o === 'object') {
				return (o as Record<string, unknown>)[p]
			}
			return undefined
		}, obj)
	}

	let count = 0
	for (const item of list) {
		if (getNestedValue(item, keyPath) === value) {
			count++
			if (count > 1) return true
		}
	}

	return false
}
/**
 * Checks if any of the object's values are falsy (empty, null, undefined, 0, false).
 *
 * @param {object} param - The object to check.
 * @returns {boolean} True if at least one value is falsy, false otherwise.
 *
 * @example
 * const hasEmpty = isSomeObjectValuesEmpty({ a: 1, b: '' });
 * // hasEmpty = true
 */
export const isSomeObjectValuesEmpty = (param: Record<string, unknown>) => {
	return Object.values(param).some(item => !item)
}
/**
 * Checks if any of the object's values are truthy and not an empty string.
 *
 * @param {object} param - The object to check.
 * @returns {boolean} True if at least one value is filled, false otherwise.
 *
 * @example
 * const hasFilled = isSomeObjectValuesFilled({ a: '', b: 'hello' });
 * // hasFilled = true
 */
export const isSomeObjectValuesFilled = (param: Record<string, unknown>) => {
	return Object.values(param).some(item => item && item !== '')
}
/**
 * Safely stringifies an object to JSON, logging any errors to the console.
 *
 * @param {object} payload - The object to stringify.
 * @returns {string | undefined} The JSON string, or undefined if an error occurred.
 *
 * @example
 * const str = stringify({ id: 1 });
 * // str = '{"id":1}'
 */
export const stringify = (payload: object) => {
	try {
		return JSON.stringify(payload)
	} catch (error) {
		console.error(error)
	}
}
/**
 * Checks if an array of objects contains any duplicate values for a given property.
 *
 * @param {{ [id: string]: string | number | unknown }[]} arr - The array of objects.
 * @param {string} property - The property key to check for duplicates.
 * @returns {boolean} True if there is a duplicate value for the property, false otherwise.
 *
 * @example
 * const isDup = isDuplicate([{ id: 1 }, { id: 2 }, { id: 1 }], 'id');
 * // isDup = true
 */
export const isDuplicate = (arr: Record<string, unknown>[], property: string) => {
	const seen = new Set()

	for (const item of arr) {
		if (seen.has(item[property])) {
			return true
		}
		seen.add(item[property])
	}

	return false
}
/**
 * Checks if the given date is more than 24 hours in the past.
 *
 * @param {number | string | Date} date - The date to check against.
 * @returns {boolean} True if the date is over 24 hours ago, false otherwise.
 *
 * @example
 * const over24h = isOver24Hours(Date.now() - 25 * 60 * 60 * 1000);
 * // over24h = true
 */
export function isOver24Hours(date: number | string | Date) {
	const saved = new Date(date).getTime()
	const twenty4hrs = 24 * 60 * 60 * 1000
	const time = !isNaN(saved) ? Date.now() - saved : 0
	return time >= twenty4hrs
}
/**
 * Checks if the given date is more than 5 days in the past.
 * Assumes a global `WAITING_PERIOD_5_DAYS` exists.
 *
 * @param {number | string | Date} date - The date to check against.
 * @returns {boolean} True if the date is over 5 days ago, false otherwise.
 *
 * @example
 * const over5Days = isOver5Days('2023-01-01');
 * // over5Days = true
 */
export function isOver5Days(date: number | string | Date) {
	const saved = new Date(date).getTime()
	const time = !isNaN(saved) ? Date.now() - saved : 0
	return time >= WAITING_PERIOD_5_DAYS
}
/**
 * Checks if the given date is more than 48 hours in the future.
 *
 * @param {number | string | Date} date - The date to check against.
 * @returns {boolean} True if the date is over 48 hours in the future, false otherwise.
 *
 * @example
 * const over48h = isOver48Hours(Date.now() + 50 * 60 * 60 * 1000);
 * // over48h = true
 */
export function isOver48Hours(date: number | string | Date) {
	const saved = new Date(date).getTime()
	const forty8hrs = 48 * 60 * 60 * 1000
	const time = !isNaN(saved) ? saved - Date.now() : 0
	return time >= forty8hrs
}
/**
 * Checks if the given timestamp is more than 30 minutes in the past.
 *
 * @param {number} key - The timestamp to check against.
 * @returns {boolean} True if the timestamp is over 30 minutes ago, false otherwise.
 *
 * @example
 * const over30m = isOver30Mins(Date.now() - 31 * 60 * 1000);
 * // over30m = true
 */
export function isOver30Mins(key: number) {
	if (isNaN(key)) {
		return false
	}
	const saved = Number(key)
	return Boolean(saved && Date.now() - saved >= 30 * 60 * 1000)
}
/**
 * Checks if the given timestamp is more than an hour in the past.
 *
 * @param {number} key - The timestamp to check against.
 * @returns {boolean} True if the timestamp is over an hour ago, false otherwise.
 *
 * @example
 * const over1h = isOverAnHour(Date.now() - 61 * 60 * 1000);
 * // over1h = true
 */
export function isOverAnHour(key: number) {
	if (isNaN(key)) {
		return false
	}
	const saved = Number(key)
	return Boolean(saved && Date.now() - saved >= 60 * 60 * 1000)
}
/**
 * Converts an array of strings into an array of objects with label and value properties.
 * The label is capitalized.
 *
 * @param {string[] | undefined} arg - The array of strings to map.
 * @returns {{ label: string; value: string }[]} The mapped array of objects, or an empty array if invalid.
 *
 * @example
 * const mapped = mapStringList(['active', 'pending']);
 * // mapped = [{ label: 'Active', value: 'active' }, { label: 'Pending', value: 'pending' }]
 */
export const mapStringList = (arg: string[] | undefined) => {
	if (Array.isArray(arg)) {
		const { toCapitalize } = StringMethods()
		const result = arg.map(item => ({ label: toCapitalize(item), value: item }))
		return result
	}
	return []
}
/**
 * Replaces a specific token ($b) in a string with a span tag to make the subsequent text bold.
 * This version applies the bolding to all text following the $b token.
 *
 * @param {string} text - The input text containing the $b token.
 * @param {string} [className] - Optional CSS classes to add to the span.
 * @returns {string} The HTML string with the bold span tag applied.
 *
 * @example
 * const html = boldAllText('Hello $b World', 'text-red-500');
 * // html = 'Hello <span class="text-red-500 font-semibold"> World</span>'
 */
export const boldAllText = (text: string, className?: string) => {
	return String(text).replace(/\$b(\s*)/, `<span class="${className} font-semibold">$1</span>`)
}
/**
 * Replaces a specific token ($b) in a string with a span tag to make the subsequent word bold.
 *
 * @param {string} text - The input text containing the $b token.
 * @param {string} [className=''] - Optional CSS classes to add to the span.
 * @returns {string} The HTML string with the bold span tag applied.
 *
 * @example
 * const html = boldText('Hello $bWorld', 'text-red-500');
 * // html = 'Hello <span class="text-red-500 font-semibold">World</span>'
 */
export const boldText = (text: string, className = '') => {
	return String(text).replace(/\$b(\S+)/g, `<span class="${className} font-semibold">$1</span>`)
}
/**
 * Merges multiple class names, resolving conflicts using tailwind-merge and clsx.
 *
 * @param {...ClassValue[]} inputs - The class values to merge.
 * @returns {string} The merged and conflict-resolved class string.
 *
 * @example
 * const classes = cn('p-2 text-black', 'text-red-500');
 * // classes = 'p-2 text-red-500'
 */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}
/**
 * Wraps a Pinia store in storeToRefs to maintain reactivity when destructuring.
 *
 * @template T - The Store type.
 * @param {T} arg - The Pinia store instance.
 * @returns {StoreToRefs<T>} An object of refs corresponding to the store's state and getters.
 *
 * @example
 * const store = useUserStore();
 * const { user } = reactor(store); // Reactive ref
 */
export const reactor = <T extends StoreGeneric>(arg: T) => storeToRefs(arg)
/**
 * Filters a select option by checking if its label contains the input string (case-insensitive).
 *
 * @param {string} input - The input string to filter by.
 * @param {any} option - The select option object containing a `label` property.
 * @returns {boolean} True if the label includes the input, false otherwise.
 *
 * @example
 * const option = { label: 'Apple', value: 'apple' };
 * const isMatch = filterSelectOption('app', option); // true
 */
export const filterSelectOption = (input: string, option: { label?: string } | undefined) => {
	return (
		String(option?.label ?? '')
			.toLowerCase()
			.indexOf(input.toLowerCase()) >= 0
	)
}

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Check if a string is a valid permission.
 *
 * @param {string} value - The string to check.
 * @returns {boolean} True if the string is a known permission.
 *
 * @example
 * const isValid = isValidPermission('users.read');
 * // isValid = true
 */
export function isValidPermission(value: string): value is PermissionValue {
	return (PERMISSIONS_FLAT as string[]).includes(value)
}

/**
 * Get permission by category and key.
 *
 * @template T - The permission category.
 * @param {T} category - The category of the permission.
 * @param {keyof (typeof PERMISSIONS)[T]} key - The key within the category.
 * @returns {PermissionValue} The corresponding permission value.
 *
 * @example
 * const perm = getPermission('users', 'read');
 * // perm = 'users.read'
 */
export function getPermission<T extends keyof typeof PERMISSIONS>(
	category: T,
	key: keyof (typeof PERMISSIONS)[T]
): PermissionValue {
	return PERMISSIONS[category][key] as PermissionValue
}

/**
 * Gets the product age limit based on the provider string.
 * Defaults to Bastion's age limit if the provider is unknown.
 *
 * @param {string} provider - The health insurance provider string.
 * @returns {number | string} The applicable age limit constant.
 *
 * @example
 * const limit = getProviderAgeLimit('hygeia');
 * // limit = ProductAgeLimit.SeniorPlan
 */
export function getProviderAgeLimit(provider: string) {
	const providerAgeLimit = {
		[Providers.bastion]: ProductAgeLimit.Bastion,
		[Providers.hygeia]: ProductAgeLimit.SeniorPlan
	}
	const s = String(provider).toLowerCase() as Providers
	return providerAgeLimit[s] || ProductAgeLimit.Bastion
}
/**
 * Calculates the difference between two dates in a specified unit.
 *
 * @param {string} [date] - The target date.
 * @param {'hour' | 'day' | 'm'} [unit='hour'] - The unit to measure the difference in ('hour', 'day', 'm' for months).
 * @param {boolean} [useDateAsInitial=false] - Whether to use the provided date as the starting point.
 * @returns {number} The difference between the dates in the requested unit.
 *
 * @example
 * const diff = getDateDifference('2023-01-01', 'day');
 * // diff = 100 (days)
 */
export const getDateDifference = (date?: string, unit: 'hour' | 'day' | 'm' = 'hour', useDateAsInitial = false) => {
	const difference = useDateAsInitial ? dayjs(date).diff(dayjs(), unit, true) : dayjs().diff(date, unit, true)
	return difference
}

type HistoryItemBase = {
	name?: string
	employee_name?: string
	duration?: string
	created_at?: string
	status?: string
	label?: string
}

export function groupHistoryByDate<T extends HistoryItemBase>(history: T[]) {
	type GroupedItem = T & { name: string; duration: string; status: string }

	const grouped: Record<
		string,
		{
			today: GroupedItem[]
			yesterday: GroupedItem[]
			two_days: GroupedItem[]
			last_week: GroupedItem[]
			older: GroupedItem[]
		}
	> = {}

	history.forEach(item => {
		const dateStr = (item.created_at || item.duration) as string
		const itemDate = dayjs(dateStr)
		const monthLabel = itemDate.format('MMMM')

		if (!grouped[monthLabel]) {
			grouped[monthLabel] = {
				today: [],
				yesterday: [],
				two_days: [],
				last_week: [],
				older: []
			}
		}

		const today = dayjs().startOf('day')
		const itemDay = itemDate.startOf('day')
		const diffDays = today.diff(itemDay, 'day')

		const schemaItem = {
			...item,
			name: item.employee_name || item.name || '',
			duration: dateStr,
			status: item.status || item.label || ''
		} as GroupedItem

		if (diffDays === 0) {
			grouped[monthLabel].today.push(schemaItem)
		} else if (diffDays === 1) {
			grouped[monthLabel].yesterday.push(schemaItem)
		} else if (diffDays === 2) {
			grouped[monthLabel].two_days.push(schemaItem)
		} else if (diffDays > 2 && diffDays <= 7) {
			grouped[monthLabel].last_week.push(schemaItem)
		} else {
			grouped[monthLabel].older.push(schemaItem)
		}
	})

	return Object.entries(grouped).map(([label, data]) => ({
		label,
		data
	}))
}
/**
 * Replaces the leading '0' of a Nigerian phone number with the '234' country code.
 *
 * @param {string} number - The phone number string.
 * @returns {string} The phone number starting with '234'.
 *
 * @example
 * const formatted = replacePhoneNumberToNaija('08012345678');
 * // formatted = '2348012345678'
 */
export const replacePhoneNumberToNaija = (number: string) => {
	const s = String(number).trim()
	return s.replace(/^0/, '234')
}
/**
 * Truncates a string to a specified number of words, appending an ellipsis if the string was cut.
 *
 * @param str - The input string to truncate.
 * @param maxWords - The maximum number of words to keep. Defaults to 12.
 * @returns The truncated string with an ellipsis if it exceeded the word limit, or the original string.
 * @Default(12)
 */
export const truncateWords = (str: string, maxWords: number = 12) => {
	if (!str) return ''
	const words = String(str).trim().split(/\s+/)
	if (words.length <= maxWords) return str
	return `${words.slice(0, maxWords).join(' ')}...`
}

/**
 * @description This function is used to render a string or number value -- returns 'n/a' if the value is null, undefined, or not a string or number
 * @param content The content to render
 * @returns The content as a string
 * */
export const renderItem = (content: null | string | number | undefined, noPlaceholder = false) => {
	if ((typeof content !== 'string' && typeof content !== 'number') || !content) {
		return (noPlaceholder ? undefined : '---') as string
	}

	return String(content).replace(/_/g, ' ')
}
